/**
 * SECURITY: localStorage PII Cleanup Utility
 * 
 * This utility removes personally identifiable information (PII) that may have been
 * stored in localStorage in previous versions of the application.
 */

import { logger } from './logger';

// List of localStorage keys that may contain PII and should be removed
const PII_KEYS_TO_REMOVE = [
  'user_name',
  'user_phone', 
  'user_email',
  'contact_name',
  'contact_phone',
  'contact_email',
  'customer_name',
  'customer_phone',
  'customer_email'
];

// List of keys that contain objects which may have PII in them
const OBJECT_KEYS_WITH_POTENTIAL_PII = [
  'chatbot_interest_data',
  'vehicleInterestData',
  'interestData'
];

/**
 * Clean up PII from localStorage
 * This function should be called during app initialization
 */
export const cleanupPIIFromLocalStorage = (): void => {
  try {
    logger.log('🔒 Starting PII cleanup from localStorage...');
    
    let cleanedKeys = 0;
    
    // Remove direct PII keys
    PII_KEYS_TO_REMOVE.forEach(key => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        cleanedKeys++;
        logger.log(`🗑️ Removed PII key: ${key}`);
      }
    });
    
    // Clean PII from object keys
    OBJECT_KEYS_WITH_POTENTIAL_PII.forEach(key => {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const data = JSON.parse(item);
          let modified = false;
          
          // Remove contact information if present
          if (data.contact) {
            delete data.contact;
            modified = true;
            logger.log(`🗑️ Removed contact data from ${key}`);
          }
          
          // Remove direct PII fields
          ['name', 'phone', 'email', 'first_name', 'full_name'].forEach(field => {
            if (data[field]) {
              delete data[field];
              modified = true;
              logger.log(`🗑️ Removed ${field} from ${key}`);
            }
          });
          
          if (modified) {
            localStorage.setItem(key, JSON.stringify(data));
            cleanedKeys++;
          }
        }
      } catch (error) {
        logger.error(`Error cleaning ${key}:`, error);
      }
    });
    
    if (cleanedKeys > 0) {
      logger.log(`🔒 PII cleanup completed. Cleaned ${cleanedKeys} items.`);
    } else {
      logger.log('🔒 PII cleanup completed. No PII found in localStorage.');
    }
    
  } catch (error) {
    logger.error('🔒 Error during PII cleanup:', error);
  }
};

// Keys that are safe to remove when quota is exceeded (non-essential cache data)
// CRITICAL: openai_response_id_direct MUST NOT be in this list - it's essential for conversation continuity!
const EXPENDABLE_KEYS = [
  'inventory_cache',
  'inventory_cache_timestamp',
  'd2c-reload-detection',
  'lastFormCloseTime',
  'Price_Disclaimer',
  'Site_Section',
  'serviceFormUrls',
  'd2c-streaming-settings',
  'sent_error_alerts',
  'sent_timeout_alerts',
  'sent_failure_alerts'
];

// Patterns for keys that can be removed (old/stale data)
const EXPENDABLE_PATTERNS = [
  /^chat_session_\d+$/,
  /^thread_.*$/,
  /^response_cache_.*$/
];

/**
 * Clear expendable localStorage data to free up quota
 * Called when localStorage quota is exceeded
 */
export const clearLocalStorageForQuota = (): number => {
  let clearedCount = 0;
  
  try {
    logger.log('🧹 Clearing expendable localStorage data for quota...');
    
    // Remove known expendable keys
    EXPENDABLE_KEYS.forEach(key => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        clearedCount++;
        logger.log(`🗑️ Removed expendable key: ${key}`);
      }
    });
    
    // Remove keys matching expendable patterns
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        for (const pattern of EXPENDABLE_PATTERNS) {
          if (pattern.test(key)) {
            keysToRemove.push(key);
            break;
          }
        }
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      clearedCount++;
      logger.log(`🗑️ Removed pattern-matched key: ${key}`);
    });
    
    logger.log(`🧹 Cleared ${clearedCount} expendable items from localStorage`);
    
  } catch (error) {
    logger.error('Error clearing localStorage for quota:', error);
  }
  
  return clearedCount;
};

/**
 * Get approximate localStorage usage in bytes
 */
export const getLocalStorageSize = (): number => {
  let totalSize = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key) || '';
        totalSize += key.length + value.length;
      }
    }
  } catch (error) {
    logger.error('Error calculating localStorage size:', error);
  }
  return totalSize * 2; // UTF-16 uses 2 bytes per character
};

/**
 * Audit localStorage for potential PII
 * This function scans localStorage and reports any keys that might contain PII
 */
export const auditLocalStorageForPII = (): string[] => {
  const potentialPIIKeys: string[] = [];
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      // Check for obvious PII key names
      const lowerKey = key.toLowerCase();
      if (lowerKey.includes('name') || 
          lowerKey.includes('phone') || 
          lowerKey.includes('email') || 
          lowerKey.includes('contact') ||
          lowerKey.includes('customer') ||
          lowerKey.includes('user')) {
        
        try {
          const value = localStorage.getItem(key);
          if (value) {
            // Check if it might contain actual PII (email patterns, phone patterns)
            if (value.includes('@') || 
                /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/.test(value) ||
                value.length > 50) { // Long strings might be names or addresses
              potentialPIIKeys.push(key);
            }
          }
        } catch (error) {
          // If we can't parse it, it might be PII
          potentialPIIKeys.push(key);
        }
      }
    }
  } catch (error) {
    logger.error('Error auditing localStorage for PII:', error);
  }
  
  return potentialPIIKeys;
};